import { Button, Group, Popover, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';
import { activeStepAtom, clearSelectionsAtom, unpolishedStepsAtom } from '~/atoms/no-code-atoms';
import EditableTextArea from '~/components/common/EditableTextArea';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { api } from '~/trpc-extra/api';

export default function StepTitle({
  step,
  selected,
  readonly,
  disabled,
  setPrevDesc,
}: {
  step: SelectedStep;
  // TODO: use global state for handling onClick
  selected?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  setPrevDesc: (prevDesc: string | undefined) => void;
}) {
  const trpcUtils = api.useUtils();

  const [deleteActionsPopoverOpened, setDeleteActionsPopoverOpened] = useState(false);
  const [unpolishedSteps, setUnpolishedStep] = useAtom(unpolishedStepsAtom);
  const clearSelections = useSetAtom(clearSelectionsAtom);
  const activeStep = useAtomValue(activeStepAtom);

  const clearStepOperations = api.operation.clearStepOperations.useMutation({
    onSuccess: (data) => {
      notifications.show({
        message: `${data.count} action${data.count === 1 ? '' : 's'} deleted successfully`,
        color: 'blue',
      });
      trpcUtils.testContent.get.invalidate().catch(() => undefined);
      setDeleteActionsPopoverOpened(false);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Error deleting actions', color: 'red' });
    },
  });

  const updateStep = api.testStep.update.useMutation({
    onSuccess: () => {
      trpcUtils.testContent.get.invalidate().catch(() => undefined);
      trpcUtils.testContent.getCode.invalidate().catch(() => undefined);
      notifications.show({ message: 'Updated the step', color: 'blue' });

      const newSet = new Set(unpolishedSteps);
      newSet.delete(step.id);
      setUnpolishedStep(newSet);

      if (step.mode === 'AI_DYNAMIC' && step.operations.length !== 0) {
        setDeleteActionsPopoverOpened(true);
      }

      setPrevDesc(undefined);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Error saving the step', color: 'red' });
    },
  });
  return (
    <Popover
      opened={deleteActionsPopoverOpened}
      onChange={setDeleteActionsPopoverOpened}
      position="right"
      withArrow
      shadow="md"
      zIndex={301}
    >
      <Popover.Target>
        <div className="w-full">
          <EditableTextArea
            inputType="textarea"
            className="w-full"
            initialVal={step.description}
            placeHolder={step.mode === 'AI_DYNAMIC' ? 'Enter step prompt' : 'Enter step description (markdown)'}
            handleSave={(newDescription: string) => {
              updateStep.mutate({ stepId: step.id, data: { description: newDescription } });
            }}
            saveOnBlur
            onEdit={() => clearSelections()}
            onBlur={() => clearSelections()}
            readonly={readonly || disabled || !(selected || activeStep?.id === step.id)}
            renderMarkdown
          />
        </div>
      </Popover.Target>

      <Popover.Dropdown>
        <div className="text-sm">
          <p className="mb-3">
            Clear{' '}
            <Tooltip
              label="AI will reference previous actions to complete the step. We recommend deleting these if the step's intent has changed."
              withArrow
              multiline
              w={215}
              classNames={{ tooltip: 'text-center' }}
              zIndex={301}
            >
              <span className="underline">reference actions</span>
            </Tooltip>
            ?
          </p>
          <Group
            justify="flex-end"
            gap="xs"
          >
            <Button
              variant="outline"
              size="compact-sm"
              onClick={() => setDeleteActionsPopoverOpened(false)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              size="compact-sm"
              onClick={() => {
                clearStepOperations.mutate({ stepId: step.id });
              }}
            >
              Clear
            </Button>
          </Group>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
